/**
 * Procedural material tiles for the 2D section preview.
 *
 * Ported from the customizer's TwoDViewer (packages/2d-handler) so the admin
 * preview and the shopper's preview show the same grain. Without these, every
 * material collapses to a flat colour here — sandblast and glass etch are both
 * pure white, so a frosted finish showed as nothing at all on a light product.
 *
 * Tiles are 256px to match the customizer's built-ins (uploaded custom patterns
 * are squared to 512 instead — see resizeToTile). Generation is lazy and cached:
 * each is a few thousand canvas ops and none are needed until a section using
 * that material is selected.
 */

const TILE_SIZE = 256

const cache: Map<string, HTMLCanvasElement> = new Map()

/** Add pixel-level noise to an ImageData buffer */
const addNoise = (imageData: ImageData, amount: number): void => {
  const d = imageData.data
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * amount
    d[i] = Math.max(0, Math.min(255, d[i] + n))
    d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + n))
    d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + n))
  }
}

/** Scatter random circles for frosted grain clusters */
const addGrainSpots = (
  ctx: CanvasRenderingContext2D,
  size: number,
  count: number,
  minR: number,
  maxR: number,
  baseBrightness: number,
  brightnessRange: number,
  alpha: number
): void => {
  ctx.globalAlpha = alpha
  for (let n = 0; n < count; n++) {
    const x = Math.random() * size
    const y = Math.random() * size
    const r = minR + Math.random() * (maxR - minR)
    const b = baseBrightness + Math.random() * brightnessRange
    ctx.fillStyle = `rgb(${b}, ${b}, ${b})`
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1.0
}

const createTile = (size: number) => {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  return { canvas, ctx: canvas.getContext('2d') }
}

/** Horizontal brush lines, used by the metallic finishes */
const brushLines = (
  ctx: CanvasRenderingContext2D,
  size: number,
  alpha: number,
  step: number,
  jitter: number,
  toneAt: () => string
): void => {
  ctx.globalAlpha = alpha
  for (let y = 0; y < size; y += step) {
    ctx.strokeStyle = toneAt()
    ctx.lineWidth = 0.5 + Math.random() * (step > 1 ? 1.5 : 1)
    ctx.beginPath()
    ctx.moveTo(0, y + (Math.random() - 0.5) * jitter)
    ctx.lineTo(size, y + (Math.random() - 0.5) * jitter)
    ctx.stroke()
  }
  ctx.globalAlpha = 1.0
}

const generators: Record<string, (size: number) => HTMLCanvasElement | null> = {
  /** Sandblast — frosted white grain with clustered spots */
  sandblast: (size) => {
    const { canvas, ctx } = createTile(size)
    if (!ctx) return null

    ctx.fillStyle = 'rgb(235, 237, 240)'
    ctx.fillRect(0, 0, size, size)

    const id = ctx.getImageData(0, 0, size, size)
    addNoise(id, 35)
    ctx.putImageData(id, 0, 0)

    addGrainSpots(ctx, size, 1200, 0.5, 3, 210, 45, 0.18)
    addGrainSpots(ctx, size, 300, 2, 5, 225, 30, 0.10)

    return canvas
  },

  /** Glass etch — very fine subtle frost, lighter than sandblast */
  glass_etch: (size) => {
    const { canvas, ctx } = createTile(size)
    if (!ctx) return null

    ctx.fillStyle = 'rgb(242, 244, 248)'
    ctx.fillRect(0, 0, size, size)

    const id = ctx.getImageData(0, 0, size, size)
    addNoise(id, 20)
    ctx.putImageData(id, 0, 0)

    addGrainSpots(ctx, size, 600, 0.3, 2, 230, 25, 0.12)

    return canvas
  },

  /** Steel — brushed metallic gray with directional grain lines */
  steel: (size) => {
    const { canvas, ctx } = createTile(size)
    if (!ctx) return null

    ctx.fillStyle = 'rgb(155, 160, 165)'
    ctx.fillRect(0, 0, size, size)

    brushLines(ctx, size, 0.09, 1, 1.5, () => {
      const b = 130 + Math.random() * 60
      return `rgb(${b}, ${b}, ${b})`
    })

    const id = ctx.getImageData(0, 0, size, size)
    addNoise(id, 18)
    ctx.putImageData(id, 0, 0)

    return canvas
  },

  /** Gold — warm brushed metal, steel's structure at a gold hue */
  gold: (size) => {
    const { canvas, ctx } = createTile(size)
    if (!ctx) return null

    ctx.fillStyle = 'rgb(196, 158, 74)'
    ctx.fillRect(0, 0, size, size)

    brushLines(ctx, size, 0.10, 1, 1.5, () => {
      const r = 170 + Math.random() * 75
      return `rgb(${Math.round(r)}, ${Math.round(r * 0.80)}, ${Math.round(r * 0.32)})`
    })

    const id = ctx.getImageData(0, 0, size, size)
    addNoise(id, 16)
    ctx.putImageData(id, 0, 0)

    return canvas
  },

  /** Anodized — slightly blue-tinted metallic grain */
  anodized: (size) => {
    const { canvas, ctx } = createTile(size)
    if (!ctx) return null

    ctx.fillStyle = 'rgb(148, 155, 175)'
    ctx.fillRect(0, 0, size, size)

    brushLines(ctx, size, 0.07, 2, 3, () => {
      const b = 140 + Math.random() * 50
      return `rgb(${b}, ${b}, ${Math.min(255, b + 15)})`
    })

    const id = ctx.getImageData(0, 0, size, size)
    addNoise(id, 16)
    ctx.putImageData(id, 0, 0)

    return canvas
  },

  /** Wood burn — brown with organic wavy grain */
  wood_burn: (size) => {
    const { canvas, ctx } = createTile(size)
    if (!ctx) return null

    ctx.fillStyle = 'rgb(90, 55, 25)'
    ctx.fillRect(0, 0, size, size)

    ctx.globalAlpha = 0.12
    for (let y = 0; y < size; y++) {
      const r = 70 + Math.random() * 50
      const g = 35 + Math.random() * 30
      const b = 10 + Math.random() * 15
      ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`
      ctx.lineWidth = 0.5 + Math.random() * 1.5
      ctx.beginPath()
      const waveAmp = 1 + Math.random() * 2
      const waveFreq = 0.02 + Math.random() * 0.03
      ctx.moveTo(0, y)
      for (let x = 0; x < size; x += 4) {
        ctx.lineTo(x, y + Math.sin(x * waveFreq + y * 0.1) * waveAmp)
      }
      ctx.stroke()
    }
    ctx.globalAlpha = 1.0

    const id = ctx.getImageData(0, 0, size, size)
    addNoise(id, 15)
    ctx.putImageData(id, 0, 0)

    return canvas
  },
}

/**
 * Tile for a built-in material, or null for materials that have none
 * ('color' keeps the artwork's own colours, 'custom' uses its uploaded file).
 */
const getMaterialTexture = (materialType: string): HTMLCanvasElement | null => {
  const cached = cache.get(materialType)
  if (cached) return cached

  const generator = generators[materialType]
  if (!generator) return null

  const tile = generator(TILE_SIZE)
  if (tile) cache.set(materialType, tile)
  return tile
}

export default getMaterialTexture
export { TILE_SIZE }
